import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Spin, Alert, message } from 'antd';
import OPMEditor from '../components/OPMEditor';
import { useOPMDraft, useSaveOPMDraft, type OPMDraftData } from '../api';
import { OntologyApi } from '../api/ontologyApi';
import { useAppStore } from '../store';
import type { OntologyGraphTree } from '../types/opm';
import {
  normalizeGraphTree,
  createGraphPayload,
} from '../components/OPMEditor/utils/graphLayerHelpers';

const OPMEditorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ontologyId = searchParams.get('id');
  const debug = searchParams.get('debug') === 'true'; // 通过URL参数控制debug模式

  // Zustand store
  const { setCurrentOntology } = useAppStore();
  
  // 状态管理
  const [currentHash, setCurrentHash] = useState<string>('');
  const [isDataReady, setIsDataReady] = useState(false);
  const [initialGraphData, setInitialGraphData] = useState<OntologyGraphTree>(normalizeGraphTree());

  // 数据获取
  const { 
    data: draftData, 
    isLoading, 
    error, 
    refetch 
  } = useOPMDraft(
    ontologyId ? parseInt(ontologyId) : 0, 
    !!ontologyId
  );

  // 保存数据
  const saveMutation = useSaveOPMDraft(
    ontologyId ? parseInt(ontologyId) : 0
  );

  // 当数据加载完成时，设置状态
  useEffect(() => {
    console.log('🔄 OPMEditorPage: 数据加载状态变化:', draftData);

    if (draftData) {
      // 如果有数据，使用返回的hash和graph数据
      if (draftData.data) {
        const normalizedGraph = normalizeGraphTree(draftData.data.graph);
        const firstLayer = normalizedGraph[0];
        console.log('📊 OPMEditorPage: 有返回数据:', {
          hash: draftData.data.hash,
          layers: normalizedGraph.length,
          nodes: firstLayer?.graphData?.nodes?.length || 0,
          edges: firstLayer?.graphData?.edges?.length || 0,
        });

        setCurrentHash(draftData.data.hash || '');
        setInitialGraphData(normalizedGraph);
        setIsDataReady(true);
      } else {
        console.log('📊 OPMEditorPage: 无返回数据，使用空画板');
        // 如果没有数据，视为新创建的画板，显示空画板
        setCurrentHash('');
        setInitialGraphData(normalizeGraphTree());
        setIsDataReady(true);
      }
    }
  }, [draftData]);

  // 获取本体详情信息
  useEffect(() => {
    const fetchOntologyDetail = async () => {
      if (ontologyId) {
        try {
          console.log('🚀 获取本体详情信息:', ontologyId);
          const detail = await OntologyApi.getOntologyDetail(parseInt(ontologyId));
          console.log('✅ 本体详情获取成功:', detail.name);

          // 设置当前本体信息到全局状态
          setCurrentOntology({
            id: detail.id,
            name: detail.name
          });
        } catch (error) {
          console.error('❌ 获取本体详情失败:', error);
          // 即使获取失败，也不影响编辑器使用
          setCurrentOntology(null);
        }
      }
    };

    fetchOntologyDetail();

    // 清理函数 - 组件卸载时清除当前本体信息
    return () => {
      setCurrentOntology(null);
    };
  }, [ontologyId, setCurrentOntology]);

  // 保存操作处理
  const handleSave = (graphData: OntologyGraphTree) => {
    console.log('准备保存，当前hash值:', currentHash);
    console.log('保存的图形数据:', graphData);
    const payload = createGraphPayload(graphData);
    const saveRequest: any = { graph: payload };
    // 只有当hash存在时才传递hash参数
    if (currentHash) {
      saveRequest.hash = currentHash;
    }

    saveMutation.mutate(
      saveRequest,
      {
        onSuccess: (response) => {
          message.success('保存成功');
          // 更新hash值，用于下次保存
          // response.data 就是新的hash字符串
          setCurrentHash(response.data);
          console.log('保存成功，新的hash值:', response.data);
        },
        onError: (error: any) => {
          const msg: string = error?.message || error?.response?.data?.message || '';
          if (msg.includes('校验进行中') || msg.includes('DRAFT_SAVE_LOCKED')) {
            message.warning('校验进行中，暂时无法保存草稿，请等待校验完成后再操作');
          } else {
            message.error('保存失败: ' + error.message);
          }
        }
      }
    );
  };

  // 如果没有提供ID，显示错误
  if (!ontologyId) {
    return (
      <div className="h-full flex items-center justify-center">
        <Alert
          message="缺少本体ID"
          description="请从本体详情页面进入编辑器"
          type="error"
          showIcon
          action={
            <button onClick={() => navigate('/home')} className="ant-btn ant-btn-primary">
              返回首页
            </button>
          }
        />
      </div>
    );
  }

  // 加载状态
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spin size="large" tip="正在加载数据..." />
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <Alert
          message="数据加载失败"
          description={error.message || '无法获取本体数据'}
          type="error"
          showIcon
          action={
            <button onClick={() => refetch()} className="ant-btn ant-btn-primary">
              重试
            </button>
          }
        />
      </div>
    );
  }

  // 数据准备就绪，渲染编辑器
  return (
    <>
      {isDataReady ? (
        <OPMEditor 
          initialData={initialGraphData}
          onSave={handleSave}
          isSaving={saveMutation.isPending}
          debug={debug}
        />
      ) : (
        <div className="h-full flex items-center justify-center">
          <Spin size="large" tip="准备编辑器..." />
        </div>
      )}
    </>
  );
};

export default OPMEditorPage;
